import React from 'react';
import { Link } from '@tanstack/react-router';
import { useForm, Controller } from 'react-hook-form';
import {
  useNotificationPreference,
  useUpdateNotificationPreference,
  useSlackChannels,
  NotificationPreference,
} from '../../../onboarding/apis/notification-preference';
import { useWorkspaces } from '../../../onboarding/apis/workspace-registry';
import { Feedback } from '@plerionhq/design-system';
import { LoadingState } from '../../../common/LoadingState';
import { LockClosedIcon, HashtagIcon } from '@heroicons/react/24/outline';
import { Select } from '../../../ui/select';
import { Button } from '@plerionhq/design-system';

import { useTranslation, Trans } from 'react-i18next';

interface NotificationPreferenceForm {
  spotlight: {
    type: NotificationPreference['type'];
    slackChannel?: {
      id: string;
      name?: string;
    };
  };
  productUpdate: {
    type: NotificationPreference['type'];
    slackChannel?: {
      id: string;
      name?: string;
    };
  };
}

interface SlackChannelSelectValue {
  label: React.ReactNode;
  value: string;
}

export const NotificationSettings: React.FC = () => {
  const { t } = useTranslation('pleri/settings');
  const {
    data: notificationPreference,
    isLoading: isLoadingNotificationPreference,
    error: fetchNotificationError,
  } = useNotificationPreference();
  const {
    mutateAsync: updateNotificationPreference,
    isPending: isUpdatingNotificationPreference,
    error: updateNotificationError,
  } = useUpdateNotificationPreference();
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useWorkspaces();

  const { handleSubmit, watch, control, setValue, register } =
    useForm<NotificationPreferenceForm>({
      defaultValues: {
        spotlight: {
          type: null,
        },
        productUpdate: {
          type: null,
        },
      },
    });

  // Update form when API data loads
  React.useEffect(() => {
    if (notificationPreference?.data?.spotlight) {
      setValue('spotlight', notificationPreference.data.spotlight);
    }
    if (notificationPreference?.data?.productUpdate) {
      setValue('productUpdate', notificationPreference.data.productUpdate);
    }
  }, [notificationPreference?.data, setValue]);

  const formData = watch();
  const [showSuccess, setShowSuccess] = React.useState(false);

  const isSlackConfigured =
    workspaces?.some(
      (w) => w.workspaceType === 'slack' && w.enabled === true,
    ) ?? false;

  const {
    data: slackChannels,
    isLoading: isLoadingSlackChannels,
    error: slackChannelsError,
  } = useSlackChannels(isSlackConfigured);

  const slackChannelSelectOptions: SlackChannelSelectValue[] =
    React.useMemo(() => {
      if (!slackChannels) {
        return [];
      }

      return [...slackChannels]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((channel) => ({
          label: (
            <span className="inline-flex items-center gap-1">
              {channel.isPrivate ? (
                <LockClosedIcon className="h-3.5 w-3.5" />
              ) : (
                <HashtagIcon className="h-3.5 w-3.5" />
              )}
              <span>{channel.name}</span>
            </span>
          ),
          value: channel.id,
        }));
    }, [slackChannels]);

  // Helper function to find slack channel by ID
  const findSlackChannelById = React.useCallback(
    (channelId: string | undefined) => {
      if (!channelId || !slackChannels) {
        return null;
      }
      return slackChannels.find((channel) => channel.id === channelId);
    },
    [slackChannels],
  );

  // Helper function to check if channel exists in select options
  const isChannelInOptions = React.useCallback(
    (channelId: string | undefined) => {
      if (!channelId) {
        return false;
      }
      return slackChannelSelectOptions.some(
        (option) => option.value === channelId,
      );
    },
    [slackChannelSelectOptions],
  );

  const onSubmit = async (data: NotificationPreferenceForm) => {
    setShowSuccess(false);

    try {
      const payload: {
        spotlight: NotificationPreference;
        productUpdate: NotificationPreference;
      } = {
        spotlight: {
          type: data.spotlight.type,
        },
        productUpdate: {
          type: data.productUpdate.type,
        },
      };

      if (data.spotlight.type == 'slack') {
        if (data.spotlight.slackChannel?.id) {
          const channel = findSlackChannelById(data.spotlight.slackChannel.id);
          payload.spotlight.slackChannel = {
            id: data.spotlight.slackChannel.id,
            name: channel?.name ?? '',
          };
        }
      }

      if (data.productUpdate.type == 'slack') {
        if (data.productUpdate.slackChannel?.id) {
          const channel = findSlackChannelById(
            data.productUpdate.slackChannel.id,
          );
          payload.productUpdate.slackChannel = {
            id: data.productUpdate.slackChannel.id,
            name: channel?.name ?? '',
          };
        }
      }

      await updateNotificationPreference(payload);
      setShowSuccess(true);
    } catch (error) {
      setShowSuccess(false);
    }
  };

  const isFormValid =
    (formData.spotlight.type !== 'slack' ||
      formData.spotlight.slackChannel !== undefined) &&
    (formData.productUpdate.type !== 'slack' ||
      formData.productUpdate.slackChannel !== undefined);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex-1 overflow-y-auto p-8">
      <div className="flex w-full justify-center">
        <div className="w-full max-w-[550px] space-y-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">
              {t('notificationSettings')}
            </h2>
            <p className="mt-1 text-base text-gray-600 dark:text-gray-400">
              {t('howDoYouWantMeToNotify')}
            </p>
          </div>

          <div className="space-y-8">
            {isLoadingNotificationPreference || isLoadingWorkspaces ? (
              <LoadingState />
            ) : fetchNotificationError ? (
              <Feedback variant="error">
                {t('iCouldnLoadYourNotificationSettingsPlease')}
              </Feedback>
            ) : (
              <>
                {/* Spotlight Section */}
                <div className="space-y-4">
                  <div>
                    {/* Header with Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                          {t('spotlights')}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {t('importantIssuesIDonWantYouTo')}
                        </p>
                      </div>
                      <Controller
                        name="spotlight.type"
                        control={control}
                        render={({ field }) => (
                          <label className="relative inline-flex cursor-pointer items-center">
                            <input
                              type="checkbox"
                              checked={
                                field.value === 'emailDM' ||
                                field.value === 'slack'
                              }
                              onChange={(e) => {
                                field.onChange(
                                  e.target.checked ? 'emailDM' : null,
                                );
                              }}
                              className="peer sr-only"
                            />
                            <div className="peer h-6 w-11 rounded-full bg-gray-300 peer-checked:bg-[#FF732E] peer-focus:ring-2 peer-focus:ring-[#FF732E] peer-focus:ring-offset-2 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full dark:bg-gray-600"></div>
                          </label>
                        )}
                      />
                    </div>

                    {/* Notification Method Selection (only if enabled) */}
                    {(formData.spotlight.type === 'emailDM' ||
                      formData.spotlight.type === 'slack') && (
                      <div className="mt-6 space-y-4">
                        {/* Email Option */}
                        <div>
                          <Controller
                            name="spotlight.type"
                            control={control}
                            render={({ field }) => (
                              <label className="flex cursor-pointer items-start gap-3">
                                <input
                                  type="radio"
                                  checked={field.value === 'emailDM'}
                                  onChange={() => {
                                    field.onChange('emailDM');
                                  }}
                                  className="mt-0.5 h-4 w-4 text-[#FF732E] focus:ring-[#FF732E]"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t('common:email')}
                                  </span>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {t('iSendImportantIssuesToYourEmail')}
                                  </p>
                                </div>
                              </label>
                            )}
                          />
                        </div>

                        {/* Slack Channel Option */}
                        <div>
                          <Controller
                            name="spotlight.type"
                            control={control}
                            render={({ field }) => (
                              <label className="flex cursor-pointer items-start gap-3">
                                <input
                                  type="radio"
                                  checked={field.value === 'slack'}
                                  onChange={() => {
                                    field.onChange('slack');
                                  }}
                                  className="mt-0.5 h-4 w-4 text-[#FF732E] focus:ring-[#FF732E]"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t('slackChannel')}
                                  </span>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                                      <Trans t={t} i18nKey="iPostImportantIssuesInASlack" />
                                                                    </p>
                                </div>
                              </label>
                            )}
                          />

                          {/* Slack Channel Select (only if Slack is selected) */}
                          {formData.spotlight.type === 'slack' && (
                            <div className="mt-3 ml-7">
                              {isSlackConfigured ? (
                                <div>
                                  {isLoadingSlackChannels ? (
                                    <div className="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[#FF732E] dark:border-gray-600" />
                                      {t('iLoadingYourChannels')}
                                    </div>
                                  ) : slackChannelsError ? (
                                    <Feedback variant="error">
                                      {t('iCouldnLoadYourSlackChannelsPlease')}
                                    </Feedback>
                                  ) : (
                                    <Select
                                      {...register('spotlight.slackChannel.id')}
                                      options={slackChannelSelectOptions}
                                      value={
                                        formData.spotlight.slackChannel?.id
                                      }
                                      placeholder={t('selectASlackChannel')}
                                      errorMessage={formData.spotlight.slackChannel?.id &&
                                      !isChannelInOptions(
                                        formData.spotlight.slackChannel?.id,
                                      )
                                        ? t('iDonHaveAccessToTheSlack', { name: formData.spotlight.slackChannel?.name })
                                        : undefined}
                                    />
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <Link
                                    to="/settings/tools/workspace/slack"
                                    className="text-action-primary text-sm font-medium"
                                  >
                                    {t('giveMeAccessToSlackToReceive')}
                                  </Link>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Updates Section */}
                <div className="space-y-4 border-t border-gray-200 pt-8 dark:border-gray-700">
                  <div>
                    {/* Header with Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                          {t('productUpdates')}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {t('iNotifyYouAboutNewFeaturesAnd')}
                        </p>
                      </div>
                      <Controller
                        name="productUpdate.type"
                        control={control}
                        render={({ field }) => (
                          <label className="relative inline-flex cursor-pointer items-center">
                            <input
                              type="checkbox"
                              checked={
                                field.value === 'emailDM' ||
                                field.value === 'slack'
                              }
                              onChange={(e) => {
                                field.onChange(
                                  e.target.checked ? 'emailDM' : null,
                                );
                              }}
                              className="peer sr-only"
                            />
                            <div className="peer h-6 w-11 rounded-full bg-gray-300 peer-checked:bg-[#FF732E] peer-focus:ring-2 peer-focus:ring-[#FF732E] peer-focus:ring-offset-2 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full dark:bg-gray-600"></div>
                          </label>
                        )}
                      />
                    </div>

                    {/* Notification Method Selection (only if enabled) */}
                    {(formData.productUpdate.type === 'emailDM' ||
                      formData.productUpdate.type === 'slack') && (
                      <div className="mt-6 space-y-4">
                        {/* Email DM Option */}
                        <div>
                          <Controller
                            name="productUpdate.type"
                            control={control}
                            render={({ field }) => (
                              <label className="flex cursor-pointer items-start gap-3">
                                <input
                                  type="radio"
                                  checked={field.value === 'emailDM'}
                                  onChange={() => {
                                    field.onChange('emailDM');
                                  }}
                                  className="mt-0.5 h-4 w-4 text-[#FF732E] focus:ring-[#FF732E]"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t('common:email')}
                                  </span>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {t('iShareNewFeaturesAndImprovementsTo')}
                                  </p>
                                </div>
                              </label>
                            )}
                          />
                        </div>

                        {/* Slack Channel Option */}
                        <div>
                          <Controller
                            name="productUpdate.type"
                            control={control}
                            render={({ field }) => (
                              <label className="flex cursor-pointer items-start gap-3">
                                <input
                                  type="radio"
                                  checked={field.value === 'slack'}
                                  onChange={() => {
                                    field.onChange('slack');
                                  }}
                                  className="mt-0.5 h-4 w-4 text-[#FF732E] focus:ring-[#FF732E]"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t('slackChannel')}
                                  </span>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                                      <Trans t={t} i18nKey="iShareNewFeaturesAndImprovementsIn" />
                                                                    </p>
                                </div>
                              </label>
                            )}
                          />

                          {/* Slack Channel Select (only if Slack is selected) */}
                          {formData.productUpdate.type === 'slack' && (
                            <div className="mt-3 ml-7">
                              {isSlackConfigured ? (
                                <div>
                                  {isLoadingSlackChannels ? (
                                    <div className="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[#FF732E] dark:border-gray-600" />
                                      {t('iLoadingYourChannels')}
                                    </div>
                                  ) : slackChannelsError ? (
                                    <Feedback variant="error">
                                      {t('iCouldnLoadYourSlackChannelsPlease')}
                                    </Feedback>
                                  ) : (
                                    <Select
                                      {...register(
                                        'productUpdate.slackChannel.id',
                                      )}
                                      options={slackChannelSelectOptions}
                                      value={
                                        formData.productUpdate.slackChannel?.id
                                      }
                                      placeholder={t('selectASlackChannel')}
                                      errorMessage={formData.productUpdate.slackChannel
                                        ?.id &&
                                      !isChannelInOptions(
                                        formData.productUpdate.slackChannel
                                          ?.id,
                                      )
                                        ? t('iDonHaveAccessToTheSlack', { name: formData.productUpdate.slackChannel?.name })
                                        : undefined}
                                    />
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <Link
                                    to="/settings/tools/workspace/slack"
                                    className="text-action-primary text-sm font-medium"
                                  >
                                    {t('giveMeAccessToSlackToReceive')}
                                  </Link>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </>
            )}
          </div>
        </div>
      </div>
      </div>
      {!isLoadingNotificationPreference &&
        !isLoadingWorkspaces &&
        !fetchNotificationError && (
          <div className="sticky bottom-0 flex-shrink-0 border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <div className="flex w-full flex-col gap-2">
              {updateNotificationError && (
                <Feedback variant="error">
                  {t('iCouldnSaveYourNotificationSettingsPlease')}
                </Feedback>
              )}
              {showSuccess && (
                <Feedback variant="success">{t('savedSuccessfully')}</Feedback>
              )}
              <div className="flex w-full justify-end font-sans">
                <Button
                  variant="primary"
                  className="mx-0 mt-0 w-auto px-4 py-1.5 text-sm"
                  isLoading={isUpdatingNotificationPreference}
                  isDisabled={!isFormValid}
                  label={t('common:save')}
                  onClick={handleSubmit(onSubmit)}
                />
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default NotificationSettings;
